import type { CSSProperties, ReactNode } from 'react';
import { Save } from 'lucide-react';
import { Button } from '../ui/Button';
import { cn } from '../ui/utils';

export type FormStickyFooterProps = {
    /** Rótulo do botão principal de salvar (padrão: "Salvar alterações"). */
    saveLabel?: string;
    /** Indicador de salvamento em andamento (ex: form.processing). */
    loading?: boolean;
    /** Texto exibido durante o carregamento (padrão: "Salvando…"). */
    loadingText?: string;
    /** Desabilita o botão de salvar. */
    disabled?: boolean;
    /** Tipo do botão principal (padrão: "submit"). */
    saveButtonType?: 'submit' | 'button';
    /** Handler opcional de clique para o botão salvar. */
    onSave?: () => void;
    /** Ícone customizado do botão salvar (padrão: <Save size={16} strokeWidth={2} />). */
    saveIcon?: ReactNode;
    /** Variante do botão principal de salvar (padrão: "primary"). */
    saveVariant?: 'primary' | 'ghost' | 'danger';
    /** URL de retorno para botão Voltar / Cancelar. */
    backHref?: string;
    /** Handler de clique para botão Voltar / Cancelar. */
    onBack?: () => void;
    /** Rótulo do botão Voltar / Cancelar. */
    backLabel?: string;
    /** Ícone customizado para o botão voltar (padrão: <ArrowLeft size={15} strokeWidth={2} />). */
    backIcon?: ReactNode;
    /** URL de retorno alternativa para Cancelar. */
    cancelHref?: string;
    /** Handler alternativo de clique para Cancelar. */
    onCancel?: () => void;
    /** Rótulo alternativo para Cancelar. */
    cancelLabel?: string;
    /** Conteúdo descritivo, atalhos ou avisos à esquerda. */
    hint?: ReactNode;
    /** Ações ou botões adicionais (renderizados antes do botão salvar). */
    actions?: ReactNode;
    /** Custom children — se informado, pode substituir ou complementar o layout padrão. */
    children?: ReactNode;
    /** Classes adicionais para o container do footer. */
    className?: string;
    /** Estilos inline adicionais. */
    style?: CSSProperties;
};

/**
 * Rodapé fixo na parte inferior da tela (sticky footer) para páginas de formulário dedicadas.
 * Padronizado com o Nexus Design System, respeitando sidebar expandida/colapsada, menu superior e telas mobile.
 */
export function FormStickyFooter({
    saveLabel = 'Salvar alterações',
    loading = false,
    loadingText = 'Salvando…',
    disabled = false,
    saveButtonType = 'submit',
    onSave,
    saveIcon = <Save size={16} strokeWidth={2} />,
    saveVariant = 'primary',
    backHref,
    onBack,
    backLabel = 'Voltar',
    backIcon,
    cancelHref,
    onCancel,
    cancelLabel,
    hint,
    actions,
    children,
    className,
    style,
}: FormStickyFooterProps) {
    if (children) {
        return (
            <div className={cn('nx-form-page-footer nx-form-sticky-footer', className)} style={style}>
                {children}
            </div>
        );
    }

    const effectiveBackHref = cancelHref ?? backHref;
    const effectiveOnBack = onCancel ?? onBack;
    const effectiveBackLabel = cancelLabel ?? backLabel;

    return (
        <div className={cn('nx-form-page-footer nx-form-sticky-footer', className)} style={style}>
            <div className="nx-form-footer-left" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                {hint ? (
                    <div className="nx-form-hint" style={{ margin: 0, fontSize: '0.8rem' }}>
                        {hint}
                    </div>
                ) : null}
            </div>

            <div className="nx-form-footer-right" style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap' }}>
                {effectiveBackHref ? (
                    <Button
                        type="button"
                        variant="ghost"
                        disabled={loading}
                        asChild
                    >
                        <a href={effectiveBackHref} style={{ textDecoration: 'none' }} className="inline-flex items-center gap-1.5">
                            {backIcon}
                            <span>{effectiveBackLabel}</span>
                        </a>
                    </Button>
                ) : effectiveOnBack ? (
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={effectiveOnBack}
                        disabled={loading}
                        className="inline-flex items-center gap-1.5"
                    >
                        {backIcon}
                        <span>{effectiveBackLabel}</span>
                    </Button>
                ) : null}

                {actions}

                <Button
                    type={saveButtonType}
                    variant={saveVariant}
                    loading={loading}
                    loadingText={loadingText}
                    disabled={disabled}
                    onClick={onSave}
                    className="inline-flex items-center gap-1.5"
                >
                    {saveIcon}
                    <span>{saveLabel}</span>
                </Button>
            </div>
        </div>
    );
}

export default FormStickyFooter;
